import { el } from './dom';
import type { RouteId } from '../core/types';
import rotateLandscapeUrl from '../assets/rotate-landscape-v1.png';
import './orientationGate.css';

/** Shared viewport gate; lives outside the stage so it cannot be letterboxed. */
export class OrientationGate {
  private readonly portrait = window.matchMedia('(orientation: portrait)');
  private readonly title = el('h2', { id: 'orientation-title' });
  private readonly copy = el('p');
  private readonly root = el('section', {
    class: 'orientation-gate',
    attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'orientation-title', tabindex: '-1' },
  }, el('div', { class: 'orientation-gate__card' },
    el('div', { class: 'orientation-gate__motion', attrs: { 'aria-hidden': 'true' } },
      el('img', { class: 'orientation-gate__icon', attrs: {
        src: rotateLandscapeUrl, alt: '', width: 256, height: 256, draggable: 'false',
      } })), this.title, this.copy));
  private route: RouteId = 'hub';
  private previousFocus: HTMLElement | null = null;
  private readonly observer = new MutationObserver(() => this.translate());
  blocked = false;

  constructor(private readonly stage: HTMLElement) {
    this.root.hidden = true;
    document.body.append(this.root);
    this.translate();
    this.portrait.addEventListener('change', this.sync);
    window.addEventListener('resize', this.sync);
    this.observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
    window.addEventListener('keydown', this.blockKeyboard, true);
    window.addEventListener('keyup', this.blockKeyboard, true);
  }

  setRoute(route: RouteId): void { this.route = route; this.sync(); }

  private translate(): void {
    const chinese = document.documentElement.lang.startsWith('zh');
    this.title.textContent = chinese ? '請旋轉螢幕' : 'Rotate your screen';
    this.copy.textContent = chinese ? '請使用橫向螢幕遊玩。' : 'Turn your device sideways to play.';
  }

  private readonly sync = (): void => {
    const next = this.route.startsWith('game') && this.portrait.matches;
    if (next === this.blocked) return;
    this.blocked = next;
    this.root.hidden = !next;
    this.stage.inert = next;
    if (next) {
      this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.root.focus({ preventScroll: true });
      document.exitPointerLock?.();
    } else if (this.previousFocus?.isConnected) {
      this.previousFocus.focus({ preventScroll: true });
    }
  };

  private readonly blockKeyboard = (event: KeyboardEvent): void => {
    // Leave browser shortcuts alone, but never pass gameplay keys through.
    if (!this.blocked || event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  dispose(): void {
    this.portrait.removeEventListener('change', this.sync);
    window.removeEventListener('resize', this.sync);
    this.observer.disconnect();
    window.removeEventListener('keydown', this.blockKeyboard, true);
    window.removeEventListener('keyup', this.blockKeyboard, true);
    this.stage.inert = false;
    this.root.remove();
  }
}
